import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { API_ROUTES } from "@shared/api";
import { apiGet } from "@client/api/client";
import { clearToken, getToken } from "@client/api/token";
import { Toaster } from "@client/components/Toaster";
import { LoginPage } from "@client/pages/Login";
import { WhatsappPage } from "@client/pages/Whatsapp";
import "@client/styles.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});

type AuthState = "checking" | "out" | "in";

function App() {
  const [auth, setAuth] = useState<AuthState>(getToken() ? "checking" : "out");

  useEffect(() => {
    if (auth !== "checking") return;
    let active = true;
    apiGet<{ username: string }>(API_ROUTES.authMe)
      .then(() => {
        if (active) setAuth("in");
      })
      .catch(() => {
        clearToken();
        if (active) setAuth("out");
      });
    return () => {
      active = false;
    };
  }, [auth]);

  if (auth === "checking") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 text-sm text-slate-500">
        Verificando sesión…
      </div>
    );
  }

  if (auth === "out") {
    return <LoginPage onLoggedIn={() => setAuth("in")} />;
  }

  return (
    <WhatsappPage
      onLogout={() => {
        clearToken();
        queryClient.clear();
        setAuth("out");
      }}
    />
  );
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error('Falta <div id="root"> en index.html');

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      <Toaster />
    </QueryClientProvider>
  </StrictMode>,
);
